/**
 * Auto E-Commerce Event Detection
 *
 * Automatically detects WooCommerce and Shopify stores and fires
 * standard e-commerce events (ViewContent, AddToCart, InitiateCheckout,
 * Purchase) without requiring manual tracking rule configuration.
 *
 * Opt-out: add data-tst-ecommerce="off" to the script tag.
 */

import { state, debugLog } from "./state.js";
import { sendEvent } from "./event-transport.js";
import { findMatchingRules } from "./url-matching.js";
import { claimConversion, orderEventId } from "./conversion-claims.js";
import { bridgeCanProduce } from "./data-layer-event-names.js";
import { claimKeyFor } from "./data-layer-event-names.js";
import { parsePrice, minorUnitsToAmount } from "./parse-price.js";
import { shopCurrency } from "./shop-currency.js";

// ── Dedup with manual tracking rules ───────────────────────────────

/**
 * Check if a manual tracking rule already fires this event name on the current URL.
 * Prevents double-firing when a customer has both a tracking rule AND auto-detection.
 */
function hasManualRule(eventName: string): boolean {
  const rules = findMatchingRules(window.location.href);
  return rules.some((r) => r.eventName === eventName);
}

/**
 * How long this detector waits before claiming, on a site that also has a
 * dataLayer we are reading.
 *
 * Long enough for a pixel plugin to announce the same conversion. Pixel Manager
 * for WooCommerce pushes within a few hundred milliseconds of page load.
 */
const BRIDGE_PRIORITY_MS = 1200;

/** Deferred sends, so a page that goes away does not take them with it. */
let deferred: Array<() => void> = [];

function flushDeferred(): void {
  const pending = deferred;
  deferred = [];
  pending.forEach((send) => send());
}

/**
 * Fire an e-commerce event, but only if no manual tracking rule covers it.
 */
function sendEcommerceEvent(data: Parameters<typeof sendEvent>[0]): void {
  if (data && data.eventName && hasManualRule(data.eventName)) {
    debugLog("ecommerce", "skipped " + data.eventName + " — manual rule exists");
    return;
  }

  // Let the shop's own dataLayer go first.
  //
  // Both sources see the same conversion and claiming is first-wins, so
  // whichever fires earliest decides what the customer's payload looks like.
  // This detector runs synchronously on page load and a pixel plugin pushes a
  // moment later, so the detector always won, and it is the poorer source: on
  // 6 August 2026 a WooCommerce shop sent ViewContent with no value while
  // Pixel Manager had `value: 49.95` sitting on the dataLayer, and AddToCart
  // went out carrying nothing but content_type. Meta answered 200 to all of it.
  //
  // Waiting is only correct because not sending is not an option: if the
  // dataLayer never announces it, this fires anyway, just later.
  const bridgeOn = !!(state.config && state.config.dataLayerBridge);
  if (data && data.eventName && bridgeOn && bridgeCanProduce(data.eventName)) {
    debugLog("ecommerce", "holding " + data.eventName + " for the dataLayer");
    const send = () => {
      deferred = deferred.filter((entry) => entry !== send);
      dispatchEcommerceEvent(data);
    };
    deferred.push(send);
    setTimeout(() => {
      if (deferred.indexOf(send) === -1) return;
      send();
    }, BRIDGE_PRIORITY_MS);
    return;
  }

  dispatchEcommerceEvent(data);
}

/** Claim the conversion and send it. The hold above has already been served. */
function dispatchEcommerceEvent(data: Parameters<typeof sendEvent>[0]): void {
  if (data && data.eventName) {
    // First-wins vs the dataLayer bridge observing the same conversion
    const orderId = data.customData && data.customData.order_id != null
      ? String(data.customData.order_id)
      : undefined;
    // The same key the dataLayer bridge uses. Let a private variant live here
    // and two sources claim the same purchase under two names, which leaves
    // the first-wins rule true only on paper.
    if (!claimConversion(data.eventName, claimKeyFor(data.eventName, orderId, window.location.href))) {
      debugLog("ecommerce", "skipped " + data.eventName + " — already claimed");
      return;
    }
    // Deterministic id so DB unique index + platform dedup catch reloads
    if (data.eventName === "Purchase" && !data.eventId) {
      data.eventId = orderEventId(orderId);
    }
    if (!data.source) data.source = "ecommerce";
  }
  sendEvent(data);
}

// ── Platform detection ─────────────────────────────────────────────

function isWooCommerce(): boolean {
  return !!(
    document.body?.classList.contains("woocommerce") ||
    document.querySelector(".woocommerce") ||
    (window as any).wc_add_to_cart_params
  );
}

function isShopify(): boolean {
  return !!((window as any).Shopify || (window as any).ShopifyAnalytics);
}

// ── Value extraction helpers ───────────────────────────────────────

/**
 * The order total on the thank-you page, classic themes and block themes.
 *
 * Only `.order-total .amount` used to be read, which is the classic template.
 * Since WooCommerce 8 a block theme is the default and that markup is gone, so
 * on a modern store we sent a Purchase with no value at all. A conversion
 * without revenue is close to worthless to an ad platform, and nothing in the
 * app says it happened: the event is there, the number is simply missing.
 *
 * The block layout puts subtotal, shipping and total in rows carrying a
 * `__label` cell. The last of those is the total; the line items above have no
 * label cell, which is what keeps them out.
 */
function findOrderTotalText(): string | undefined {
  const classic = document.querySelector(".order-total .amount, .order-total .woocommerce-Price-amount");
  if (classic?.textContent) return classic.textContent;

  const labels = document.querySelectorAll(".wc-block-order-confirmation-totals__label");
  const lastLabel = labels[labels.length - 1];
  if (!lastLabel) return undefined;

  const row = lastLabel.closest("tr");
  const total = row?.querySelector(".wc-block-order-confirmation-totals__total");
  return total?.textContent ?? undefined;
}

/**
 * The price of the product being viewed, classic themes and block themes.
 *
 * `.price .amount` is the classic template and returns nothing on a block
 * theme, so a modern WooCommerce shop sent ViewContent and AddToCart with no
 * value at all. Measured on a block shop on 6 August 2026: `.price .amount`
 * matched zero elements, `.woocommerce-Price-amount` matched two.
 *
 * Two is the trap. The second was an upsell further down the page, and the
 * whole `.product` container holds both. So the search is scoped to the
 * summary, which is the block holding the price of THIS product. Reading the
 * page at large would quietly report the price of a product nobody looked at.
 */
function findProductPriceText(root?: Element | null): string | undefined {
  // Scoped to the card that was clicked when there is one.
  //
  // Searching the whole document is right on a product page and wrong
  // everywhere else: a shop or category page renders a price block inside every
  // card, so `querySelector` returns the FIRST card's price no matter which
  // product was added. Measured on the test shop: adding the 19.00 item
  // reported 49.95, because that is what the first card cost. An absent value
  // is poor; a confident wrong one is worse, because an ad platform bids on it.
  const scope: ParentNode = root ?? document;

  const summary = scope.querySelector(
    ".wp-block-woocommerce-product-price, .summary, .entry-summary",
  );
  if (summary) {
    // The sale price, when there is one. The `ins` element is not always
    // inside a `.price` wrapper: block markup puts `del` and `ins` straight
    // into the price block, so requiring the wrapper found nothing there and
    // the fallback below then returned the struck-through original.
    const onSale = summary.querySelector("ins .amount, ins .woocommerce-Price-amount");
    if (onSale?.textContent) return onSale.textContent;
    const price = summary.querySelector(".price .amount, .woocommerce-Price-amount");
    if (price?.textContent) return price.textContent;
  }

  const classicOnSale = scope.querySelector(".product .price ins .amount, .price ins .amount");
  if (classicOnSale?.textContent) return classicOnSale.textContent;
  const classic = scope.querySelector(".product .price .amount, .price .amount");
  return classic?.textContent ?? undefined;
}

/** The card a clicked add-to-cart button belongs to, when it is in a listing. */
function productCardFor(button: Element): Element | null {
  return button.closest(".wc-block-product, .wp-block-post, li.product, .product");
}

/**
 * The id of the product being viewed.
 *
 * `data-product_id` is the classic attribute and is absent on a block theme.
 * The add-to-cart form carries it in every theme, and the container's
 * `post-<id>` class is the last resort.
 */
function findProductId(): string | undefined {
  const formField = document.querySelector('form.cart input[name="add-to-cart"], form.cart button[name="add-to-cart"]');
  const fromForm = (formField as HTMLInputElement | null)?.value;
  if (fromForm) return fromForm;

  const productEl = document.querySelector(".product");
  const attr = productEl?.getAttribute("data-product_id");
  if (attr) return attr;
  return productEl?.classList.toString().match(/post-(\d+)/)?.[1];
}

interface WooCart {
  readonly value: number | undefined;
  readonly currency: string | undefined;
  readonly contentIds: string[];
  readonly numItems: number;
}

/**
 * The cart, from WooCommerce's own Store API.
 *
 * Every price in that response is an integer in minor units alongside a
 * `currency_minor_unit`, so 4995 with unit 2 is 49.95. Reading it as a plain
 * number would report a cart a hundred times too big, which is exactly the kind
 * of number an ad platform will happily optimise towards.
 *
 * Resolves to null on any failure. A checkout start without a value is poor;
 * one that never fires because a fetch went wrong is worse.
 */
function readWooCart(): Promise<WooCart | null> {
  return fetch("/wp-json/wc/store/v1/cart", { credentials: "same-origin" })
    .then((response) => (response.ok ? response.json() : null))
    .then((cart: any) => {
      if (!cart || !cart.totals) return null;
      const value = minorUnitsToAmount(cart.totals.total_price, cart.totals.currency_minor_unit);
      const items: any[] = Array.isArray(cart.items) ? cart.items : [];
      return {
        value,
        currency: cart.totals.currency_code || undefined,
        contentIds: items.map((item) => String(item.id)).filter(Boolean),
        numItems: Number(cart.items_count) || items.length,
      } satisfies WooCart;
    })
    .catch(() => null);
}

/**
 * The shop's currency.
 *
 * `woocommerce_params.currency` does not exist and never has. That object holds
 * four keys: ajax_url, wc_ajax_url and two password labels. Reading a currency
 * off it therefore always fell through to the "EUR" default, so every
 * auto-detected WooCommerce shop reported euros, including dollar and pound
 * shops. Verified in the browser on a live store on 6 August 2026.
 *
 * That is silent corruption of the one number ad platforms optimise on: the
 * amount is right and the unit is wrong, so a $100 order is bid against as if
 * it were EUR 100, and nothing anywhere reports a problem.
 *
 * `wcSettings.currency.code` is what WooCommerce actually publishes, and a shop
 * running Pixel Manager states it too. EUR stays as the last resort rather than
 * sending nothing, because Meta rejects a value without a currency.
 */
function findWooCurrency(): string {
  return shopCurrency() ?? "EUR";
}

function getOrderIdFromUrl(): string | undefined {
  // WooCommerce: /checkout/order-received/123/
  const match = window.location.pathname.match(/order-received\/(\d+)/);
  return match ? match[1] : undefined;
}

// ── WooCommerce tracking ───────────────────────────────────────────

function installWooCommerceTracking(): void {
  debugLog("ecommerce", "WooCommerce detected");

  const body = document.body;
  if (!body) return;

  // ViewContent — product page
  if (body.classList.contains("single-product")) {
    const productEl = document.querySelector(".product");
    if (productEl) {
      const productId = findProductId();
      const value = parsePrice(findProductPriceText());
      const name = document.querySelector(".product_title")?.textContent?.trim();

      sendEcommerceEvent({
        eventName: "ViewContent",
        eventValue: value,
        currency: findWooCurrency(),
        customData: {
          content_ids: productId ? [productId] : undefined,
          content_type: "product",
          content_name: name,
        },
      });
    }
  }

  // AddToCart — AJAX add to cart (WooCommerce fires jQuery event)
  if ((window as any).jQuery) {
    try {
      (window as any).jQuery(document.body).on(
        "added_to_cart",
        (_event: any, _fragments: any, _hash: any, button: any) => {
          // The button attributes are the classic shop-archive markup. On a
          // block theme, and on the single-product page in any theme, they are
          // absent, so the page itself is the fallback for both.
          const productId = button?.attr?.("data-product_id") || findProductId();
          const priceAttr = button?.attr?.("data-price");
          const node = button && button[0] ? (button[0] as Element) : null;
          const value = priceAttr
            ? parseFloat(priceAttr)
            : parsePrice(findProductPriceText(node ? productCardFor(node) : null));

          debugLog("ecommerce", "WooCommerce AddToCart", productId);

          sendEcommerceEvent({
            eventName: "AddToCart",
            eventValue: value,
            currency: findWooCurrency(),
            customData: {
              content_ids: productId ? [productId] : undefined,
              content_type: "product",
            },
          });
        },
      );
    } catch { /* jQuery not available or event binding failed */ }
  }

  // AddToCart — fallback for non-AJAX add to cart buttons
  document.addEventListener("click", (e) => {
    const target = e.target as Element;
    if (!target) return;

    const button = target.closest(".add_to_cart_button, .single_add_to_cart_button");
    if (!button) return;

    // Skip if jQuery handler already caught this (AJAX carts)
    if (button.classList.contains("ajax_add_to_cart")) return;

    // Same story as the AJAX path: on a block theme this button carries no
    // data attributes at all, so an AddToCart went to Meta with nothing but
    // content_type on it. Measured on a real shop on 6 August 2026.
    const productId = button.getAttribute("data-product_id") || findProductId();
    const value = parsePrice(findProductPriceText(productCardFor(button)));
    debugLog("ecommerce", "WooCommerce AddToCart (non-AJAX)", productId, value);

    sendEcommerceEvent({
      eventName: "AddToCart",
      eventValue: value,
      currency: findWooCurrency(),
      customData: {
        content_ids: productId ? [productId] : undefined,
        content_type: "product",
      },
    });
  }, true);

  // Purchase — order received / thank-you page
  // Some themes don't set the body class, so also check URL pattern as fallback
  const isOrderReceived = body.classList.contains("woocommerce-order-received") ||
    window.location.pathname.includes("order-received");

  // InitiateCheckout — checkout page, but NOT the thank-you page.
  //
  // WooCommerce keeps `woocommerce-checkout` on the body as soon as the
  // thank-you page sits under /checkout/, which is the default setup. On
  // 5 August 2026 a test store showed a checkout start arriving alongside
  // every purchase because of it. That is not a double count of the same
  // event but a conversion that never happened: it pollutes the funnel and,
  // worse, it teaches the ad platform to optimise on the wrong signal.
  if (body.classList.contains("woocommerce-checkout") && !isOrderReceived) {
    debugLog("ecommerce", "WooCommerce InitiateCheckout");
    // The cart comes from WooCommerce's own Store API rather than the page.
    // This event used to go to Meta with custom_data completely empty: no
    // value, no currency, no products, nothing to optimise on, and a 200 back
    // so nobody could tell. The block checkout renders its totals in a React
    // tree that is not there yet when this runs, so scraping was never going
    // to work; the API answers with exact figures in minor units.
    readWooCart().then((cart) => {
      sendEcommerceEvent({
        eventName: "InitiateCheckout",
        eventValue: cart?.value,
        currency: cart?.currency || findWooCurrency(),
        customData: cart
          ? {
              content_ids: cart.contentIds.length > 0 ? cart.contentIds : undefined,
              content_type: "product",
              num_items: cart.numItems,
            }
          : undefined,
      });
    });
  }

  if (isOrderReceived) {
    const orderId = getOrderIdFromUrl();
    const value = parsePrice(findOrderTotalText());
    const itemCount = document.querySelectorAll(".order_item, .woocommerce-table__line-item").length;

    // The products in the order.
    //
    // This used to read the product id out of a `?p=123` query string on the
    // order-item links. WordPress only writes that shape when a site runs plain
    // permalinks, which almost nobody does: the default is `/product/slug/` and
    // the match then never succeeds. So Purchase went to Meta with no product
    // identity at all, on essentially every real shop, and a purchase without
    // content_ids cannot feed a catalogue ad.
    //
    // Three shapes are tried, in order of how much they can be trusted. A data
    // attribute is the shop stating the id; the `?p=` form is the same thing
    // through the URL; the slug is a last resort and is NOT used, because Meta
    // matches content_ids against the catalogue and a slug matches nothing
    // there. Sending it would look like coverage and behave like noise.
    const contentIds: string[] = [];
    const rows = document.querySelectorAll(
      ".order_item, .woocommerce-table__line-item, .wc-block-order-confirmation-totals__product",
    );
    rows.forEach((row) => {
      const tagged = row.getAttribute("data-product-id") || row.getAttribute("data-product_id");
      if (tagged) {
        contentIds.push(tagged);
        return;
      }
      const link = row.querySelector("a[href]");
      const href = link?.getAttribute("href") || "";
      const idMatch = href.match(/[?&](?:p|product_id|add-to-cart)=(\d+)/);
      if (idMatch) contentIds.push(idMatch[1]);
    });

    // A page at this URL that shows no order is not a purchase.
    //
    // WooCommerce hides the order from a guest once the grace period has
    // passed, and from a known customer viewing it logged out. What comes back
    // is an ordinary 200 at the correct order-received URL with no total and no
    // line items on it. Reproduced on the test shop on 6 August 2026: to
    // anything reading the URL or the DOM it is indistinguishable from success.
    //
    // Sending anyway would report a purchase with no revenue and no products,
    // which an ad platform accepts and cannot use, and which quietly claims the
    // ord_<id> that the real one would have carried. Better to say nothing and
    // let the server-side path, which knows the order, report it.
    const pageShowsAnOrder = value != null || rows.length > 0 || itemCount > 0;
    if (!pageShowsAnOrder) {
      debugLog("ecommerce", "order-received page shows no order; leaving it to the server");
      return;
    }

    debugLog("ecommerce", "WooCommerce Purchase", orderId, value);

    sendEcommerceEvent({
      eventName: "Purchase",
      eventValue: value,
      currency: findWooCurrency(),
      customData: {
        order_id: orderId,
        num_items: itemCount || undefined,
        content_type: "product",
        content_ids: contentIds.length > 0 ? contentIds : undefined,
      },
    });
  }
}

// ── Shopify tracking ───────────────────────────────────────────────

function installShopifyTracking(): void {
  debugLog("ecommerce", "Shopify detected");

  const shopify = (window as any).Shopify;
  const analytics = (window as any).ShopifyAnalytics;
  const pathname = window.location.pathname;

  // ViewContent — product page
  if (pathname.includes("/products/") && analytics?.meta?.product) {
    const product = analytics.meta.product;
    sendEcommerceEvent({
      eventName: "ViewContent",
      eventValue: product.variants?.[0]?.price ? parseFloat(product.variants[0].price) : undefined,
      currency: shopCurrency() ?? "EUR",
      customData: {
        content_ids: product.id ? [String(product.id)] : undefined,
        content_type: "product",
        content_name: product.title,
      },
    });
  }

  // AddToCart — intercept add-to-cart form submissions
  document.addEventListener("submit", (e) => {
    const form = e.target as HTMLFormElement;
    if (!form || !form.action?.includes("/cart/add")) return;

    const productIdInput = form.querySelector('input[name="id"], select[name="id"]') as HTMLInputElement | null;
    const productId = productIdInput?.value;

    debugLog("ecommerce", "Shopify AddToCart", productId);

    sendEcommerceEvent({
      eventName: "AddToCart",
      currency: shopCurrency() ?? "EUR",
      customData: {
        content_ids: productId ? [productId] : undefined,
        content_type: "product",
      },
    });
  }, true);

  // InitiateCheckout — checkout page
  if (pathname.includes("/checkout") && !pathname.includes("/thank_you")) {
    debugLog("ecommerce", "Shopify InitiateCheckout");
    sendEcommerceEvent({ eventName: "InitiateCheckout" });
  }

  // Purchase — thank you page
  if (pathname.includes("/thank_you") || pathname.match(/\/orders\/[^/]+$/)) {
    const checkout = shopify?.checkout;
    if (checkout) {
      debugLog("ecommerce", "Shopify Purchase", checkout.order_id);

      sendEcommerceEvent({
        eventName: "Purchase",
        eventValue: checkout.total_price ? parseFloat(checkout.total_price) : undefined,
        currency: checkout.currency || shopCurrency() || "EUR",
        customData: {
          order_id: checkout.order_id ? String(checkout.order_id) : undefined,
          num_items: checkout.line_items?.length,
          content_type: "product",
          content_ids: checkout.line_items?.map((item: any) => String(item.variant_id || item.product_id)),
        },
      });
    } else {
      // Fallback: try Shopify.order or data embedded in the page
      const order = shopify?.order;
      const orderIdMeta = document.querySelector('meta[name="shopify-checkout-order-id"]');
      const totalMeta = document.querySelector('meta[name="shopify-checkout-total"]');

      debugLog("ecommerce", "Shopify Purchase (fallback)");

      sendEcommerceEvent({
        eventName: "Purchase",
        eventValue: order?.total_price
          ? parseFloat(order.total_price)
          : totalMeta
            ? parseFloat(totalMeta.getAttribute("content") || "")
            : undefined,
        currency: shopCurrency() ?? "EUR",
        customData: {
          order_id: order?.id
            ? String(order.id)
            : orderIdMeta?.getAttribute("content") || undefined,
          content_type: "product",
        },
      });
    }
  }
}

// ── Public API ─────────────────────────────────────────────────────

/**
 * Install e-commerce auto-detection.
 * Call after DOM is ready and config is loaded.
 * Automatically identifies WooCommerce or Shopify and fires events.
 */
export function installEcommerceDetection(): void {
  // Opt-out, read at bootstrap from our own tag; see state.ecommerceDisabled.
  if (state.ecommerceDisabled) {
    debugLog("ecommerce", "opt-out via data-tst-ecommerce=off");
    return;
  }

  // A page that goes away while an event is still waiting for the dataLayer
  // takes that event with it. Clicking add-to-cart on a block theme navigates,
  // so without this the hold would have turned a working AddToCart into a
  // missing one, which is precisely the trade this change refuses to make.
  window.addEventListener("pagehide", flushDeferred);

  if (isWooCommerce()) {
    installWooCommerceTracking();
    return;
  }

  if (isShopify()) {
    installShopifyTracking();
    return;
  }

  debugLog("ecommerce", "no e-commerce platform detected");
}
